// Copyright 2026. WebPros International GmbH. All rights reserved.

import { createCustomAction } from 'typesafe-actions';
import { Dispatch } from 'redux';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { paginateList } from 'common/actions/actionsWrapper';
import { IPaginateApiResponse } from 'common/api/resources/Response';
import * as types from 'common/modules/failoverDomain/constants/types';
import {
    failoverDomains,
    IFailoverDomainCreateRequest,
    IFailoverDomainPatchRequest,
    IFailoverDomainResponse,
} from 'common/api/resources/FailoverDomain';
import {
    setIsLoading,
    unsetIsLoading,
} from 'common/modules/app/loadingFlags/actions';
import { bakeForegroundToast } from 'common/modules/app/toaster/actions';
import { INTENT_TYPE } from 'common/constants';
import { HTTP_CODES } from 'common/api/constants';
import { IPaginatedWithSearch } from 'common/api/resources/Request/request';

export const setFailoverDomainList = createCustomAction(
    types.SET_FAILOVER_DOMAIN_LIST,
    (data: IPaginateApiResponse<IFailoverDomainResponse[]>) => ({ payload: data })
);

export const updateFailoverDomainItem = createCustomAction(
    types.UPDATE_FAILOVER_DOMAIN_ITEM,
    (data: IFailoverDomainResponse) => ({ payload: data })
);

export const setFailoverDomainItemIsLoading = createCustomAction(
    types.SET_FAILOVER_DOMAIN_ITEM_IS_LOADING,
    (id: number) => ({ payload: id })
);

export const unsetFailoverDomainItemIsLoading = createCustomAction(
    types.UNSET_FAILOVER_DOMAIN_ITEM_IS_LOADING,
    (id: number) => ({ payload: id })
);

export const removeFailoverDomainItem = createCustomAction(
    types.REMOVE_FAILOVER_DOMAIN_ITEM,
    (id: number) => ({ payload: id })
);

export const getFailoverDomains = (params?: IPaginatedWithSearch) => async (dispatch: Dispatch) =>
    await paginateList({
        dispatch,
        loadingFlag: LOADING_FLAGS.FAILOVER_DOMAIN_LIST,
        action: setFailoverDomainList,
        apiCall: () => failoverDomains.list(params),
    });

export const toggleActiveState = (
    id: number,
    data: IFailoverDomainPatchRequest
) => async (dispatch: Dispatch) => {
    dispatch(setFailoverDomainItemIsLoading(id));

    try {
        const result = await failoverDomains.patch(id, data);
        dispatch(updateFailoverDomainItem(result.data.data));
        return result;
    } finally {
        dispatch(unsetFailoverDomainItemIsLoading(id));
    }
};

export const deleteFailoverDomain = (id: number) => async (dispatch: Dispatch) => {
    dispatch(setFailoverDomainItemIsLoading(id));

    try {
        await failoverDomains.remove(id);
        dispatch(removeFailoverDomainItem(id));
        bakeForegroundToast(
            INTENT_TYPE.SUCCESS,
            'failoverDomain.toasts.failoverDomainDeleted'
        )(dispatch);
    } catch {
        bakeForegroundToast(
            INTENT_TYPE.DANGER,
            'failoverDomain.deleteError'
        )(dispatch);
    } finally {
        dispatch(unsetFailoverDomainItemIsLoading(id));
    }
};

export const createFailoverDomain = (
    data: IFailoverDomainCreateRequest
) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_CREATE));

    try {
        const result = await failoverDomains.create(data);

        if ([HTTP_CODES.CREATED, HTTP_CODES.OK].includes(result.status)) {
            bakeForegroundToast(
                INTENT_TYPE.SUCCESS,
                'failoverDomain.toasts.failoverDomainSaved'
            )(dispatch);
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_CREATE));
    }
};

export const updateFailoverDomain = (
    id: number,
    data: IFailoverDomainPatchRequest
) => async (dispatch: Dispatch) => {
    dispatch(setIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_UPDATE));

    try {
        const result = await failoverDomains.patch(id, data);
        dispatch(updateFailoverDomainItem(result.data.data));

        if (result.status === HTTP_CODES.OK) {
            bakeForegroundToast(
                INTENT_TYPE.SUCCESS,
                'failoverDomain.toasts.failoverDomainSaved'
            )(dispatch);
        }

        return result;
    } finally {
        dispatch(unsetIsLoading(LOADING_FLAGS.FAILOVER_DOMAIN_UPDATE));
    }
};
